import { Units } from '@turf/helpers';
import { circle as turfCircle } from '@turf/turf';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import {
  getAisWebsocketToken,
  getAisWebsocketUrl,
} from '../../../../api/internal-ais-api/internal-ais-api';
import {
  intialiseAisApiWebsocket,
  locationMessageToVessel,
} from '../../../../api/internal-ais-api/internal-ais-api-helpers';
import {
  getVesselsDAAS,
  intialiseVesselsByLocationWebsocket,
  mergeMessagesOntoVessels,
} from '../../../../api/vessels';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import useAccessControl from '../../../../hooks/access-control/useAccessControl';
import useMapLayerCircle from '../../../../map/hooks/use-map-layer-circle';
import { DMSToDecimal } from '../../../../map/map-controls.utils';
import MapLayer from '../../../../map/map-layer-manager/map-layer.enum';
import MapHelpers from '../../../../map/map.utils';
import {
  MaritimeAisApiLocation,
  MessageData,
  VesselData,
} from '../../../../models/maritime-ais-api';
import { LocationMessage } from '../../../../models/vessels/internal-ais-api.model';
import { Vessel } from '../../../../models/vessels/vessel.model';
import { AppDispatch } from '../../../../store';
import {
  clearNearbyVessels,
  maritimeAisResponseToVessels,
  renderNearbyVessels,
} from '../../../../utils/vessels.utils';
import {
  NearbyVesselItem,
  NearbyVesselsHookResult,
  NearbyVesselsWebsocketProps,
} from '../nearby-vessels-data.types';
import { NearbyVesselsFormValues } from '../nearby-vessels-form/nearby-vessels-form-values';
import {
  appendVessels,
  setError,
  setFormValues,
  setLoading,
  setVessels,
} from '../nearby-vessels.slice';
import useLongWaitTimer from './use-long-wait-timer';

const CALLER_FEATURE = 'nearby-vessels';
const HISTORY_WEBSOCKET_BASE =
  process.env.REACT_APP_HISTORY_WEBSOCKET_BASE || '';

// ── Pure helpers ──────────────────────────────────────────────────────────

/**
 * Build a search circle GeoJSON polygon from the provided form values.
 *
 * The returned polygon is used both as the query geometry sent to the APIs
 * and as the area to zoom the map to.
 */
function buildSearchCircle(values: NearbyVesselsFormValues) {
  const centre = values.centrePoint
    ? [
        DMSToDecimal(values.centrePoint.longitude) || 0,
        DMSToDecimal(values.centrePoint.latitude) || 0,
      ]
    : [0, 0];
  return turfCircle(centre, values.radius, {
    steps: 16,
    units: 'nauticalmiles' as Units,
  });
}

/**
 * Normalize domain `Vessel` objects into the simplified `NearbyVesselItem`
 * shape used by the UI list. Handles null/undefined names and numeric ids.
 */
function toVesselItems(vessels: Vessel[] | null): NearbyVesselItem[] {
  return (vessels ?? []).map((v) => ({
    key: v.vessel_id,
    name: v.name || null,
    mmsi: v.mmsi as number | undefined,
    imo: v.imo as number | undefined,
  }));
}

// ── V1: Maritime AIS / DAAS ───────────────────────────────────────────────

/**
 * Factory that creates a submit handler for the V1 implementation
 * (Maritime AIS / DAAS). The produced handler dispatches loading state,
 * saves form values, opens a history websocket and zooms the map to the
 * requested search area.
 */
function createV1SubmitHandler(
  dispatch: AppDispatch,
  setWebsocketId: (id: string | undefined) => void
): (values: NearbyVesselsFormValues) => void {
  return (values) => {
    dispatch(setLoading(true));
    dispatch(setError(false));
    dispatch(setFormValues(values));
    const circle = buildSearchCircle(values);
    const wsId = `history-websocket-${uuid()}`;
    setWebsocketId(wsId);
    intialiseVesselsByLocationWebsocket(
      circle.geometry.coordinates,
      values.timeSinceLastAIS,
      wsId
    );
    MapHelpers.zoomToPolygon(circle.geometry);
  };
}

/**
 * Factory that creates a message handler for V1 history websocket
 * responses. It parses each message batch, fetches additional vessel
 * details from DAAS, merges messages on to vessel records and appends
 * them to the store. Handles end-of-data and error states.
 */
function createV1MessageHandler(
  dispatchRef: MutableRefObject<AppDispatch>,
  setWebsocketId: (id: string | undefined) => void
): (result: MessageEvent<unknown>) => Promise<void> {
  return async (result) => {
    try {
      const { metadata, data: messageDataResponse } = JSON.parse(
        result.data as string
      );

      if (!messageDataResponse) {
        dispatchRef.current(setError(true));
      }

      if (metadata?.endOfData) {
        setWebsocketId(undefined);
        dispatchRef.current(setLoading(false));
        dispatchRef.current(setError(false));
        return;
      }

      const mmsis = new Set<string>(
        messageDataResponse.map((msg: MessageData) => msg.mmsi.toString())
      );
      const daasResponse = await getVesselsDAAS({ mmsis: Array.from(mmsis) });
      const vesselDataMap: Record<string, VesselData> = daasResponse.reduce(
        (acc: Record<string, VesselData>, v) => {
          acc[v.vesselId] = v;
          return acc;
        },
        {}
      );
      const merged: Record<string, MaritimeAisApiLocation> =
        mergeMessagesOntoVessels(vesselDataMap, messageDataResponse, false);
      dispatchRef.current(
        appendVessels(maritimeAisResponseToVessels({ data: merged }))
      );
    } catch {
      dispatchRef.current(setLoading(false));
      dispatchRef.current(setError(true));
    }
  };
}

/**
 * Build websocket props for the V1 history websocket. Returns `null`
 * when there is no active websocket id or when the history websocket base
 * URL is not configured.
 */
function buildV1WebsocketProps(
  websocketId: string | undefined,
  onMessageReceived: (e: MessageEvent<unknown>) => void
): NearbyVesselsWebsocketProps | null {
  if (!websocketId || !HISTORY_WEBSOCKET_BASE) return null;
  return {
    url: new URL(
      `${HISTORY_WEBSOCKET_BASE}?clientId=${websocketId}&dataSource=aisDatabase`
    ),
    signUrl: true,
    onMessageReceived,
  };
}

// ── V2: Internal AIS websocket ────────────────────────────────────────────

/**
 * Factory that creates a submit handler for the V2 implementation
 * (internal AIS websocket). Ensures websocket base URL and token are
 * available, initialises the AIS websocket query dispatch, mounts the
 * websocket and zooms the map. Errors are surfaced via the slice state.
 */
function createV2SubmitHandler(
  dispatch: AppDispatch,
  aisBaseUrl: string | undefined,
  setAisBaseUrl: (url: string) => void,
  setWsUrl: (url: string) => void,
  onDispatchQueryReady: (fn: () => Promise<boolean>) => void,
  setMountWebsocket: (active: boolean) => void
): (values: NearbyVesselsFormValues) => void {
  return async (values) => {
    dispatch(setLoading(true));
    dispatch(setError(false));
    dispatch(setFormValues(values));
    const circle = buildSearchCircle(values);

    try {
      let baseUrl = aisBaseUrl;
      if (!baseUrl) {
        const urlResponse = await getAisWebsocketUrl();
        baseUrl = urlResponse.ws_url;
        setAisBaseUrl(baseUrl);
      }
      const { token } = await getAisWebsocketToken({
        callerFeature: CALLER_FEATURE,
      });
      setWsUrl(`${baseUrl}?token=${token}`);

      const { dispatchQuery } = await intialiseAisApiWebsocket(
        circle.geometry,
        token,
        CALLER_FEATURE,
        values.timeSinceLastAIS
      );
      onDispatchQueryReady(dispatchQuery);

      setMountWebsocket(true);
      MapHelpers.zoomToPolygon(circle.geometry);
    } catch {
      dispatch(setError(true));
      dispatch(setLoading(false));
    }
  };
}

/**
 * Factory that creates a message handler for the V2 internal AIS websocket.
 * Parses incoming location messages, converts them to vessel records and
 * appends them to the store. Keeps `loading` true until `endOfData` so the
 * UI only renders when the stream completes. On errors the websocket is
 * unmounted and state updated.
 */
function createV2MessageHandler(
  dispatchRef: MutableRefObject<AppDispatch>,
  setMountWebsocket: (active: boolean) => void
): (result: MessageEvent<unknown>) => Promise<void> {
  return async (result) => {
    try {
      const {
        metadata,
        data: messageDataResponse,
      }: { metadata: any; data: LocationMessage[] } = JSON.parse(
        result.data as string
      );

      if (!messageDataResponse) {
        dispatchRef.current(setError(true));
      }

      if (metadata?.endOfData) {
        dispatchRef.current(setError(false));
        dispatchRef.current(setLoading(false));
        setMountWebsocket(false);
        return;
      }

      // Do NOT call setLoading(false) here — keep loading until endOfData so
      // the map and results list only render once the stream is complete.
      dispatchRef.current(
        appendVessels(
          messageDataResponse
            .map(locationMessageToVessel)
            .filter((v): v is NonNullable<typeof v> => v !== null)
        )
      );
    } catch {
      dispatchRef.current(setLoading(false));
      dispatchRef.current(setError(true));
      setMountWebsocket(false);
    }
  };
}

/**
 * Build websocket props for the V2 internal AIS websocket. Wires
 * connection lifecycle callbacks (open/error/close) to manage dispatching
 * the prepared query and to reflect connection state in the store.
 */
function buildV2WebsocketProps(
  mountWebsocket: boolean,
  wsUrl: string | undefined,
  dispatchRef: MutableRefObject<AppDispatch>,
  dispatchQueryRef: MutableRefObject<(() => Promise<boolean>) | null>,
  setMountWebsocket: (active: boolean) => void,
  onMessageReceived: (e: MessageEvent<unknown>) => void
): NearbyVesselsWebsocketProps | null {
  if (!mountWebsocket || !wsUrl) return null;
  return {
    url: new URL(wsUrl),
    signUrl: false,
    onConnectionOpened: () => {
      dispatchQueryRef.current?.();
    },
    onConnectionError: () => {
      dispatchRef.current(setLoading(false));
      dispatchRef.current(setError(true));
      setMountWebsocket(false);
    },
    onConnectionClosed: () => setMountWebsocket(false),
    onMessageReceived,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────

/**
 * Unified nearby-vessels hook. Reads the `canAccessFeatureAisApiNearbyVessels`
 * feature flag and delegates to either the v1 (Maritime AIS / DAAS) or v2
 * (internal AIS websocket) implementation accordingly.
 *
 * All API-specific logic lives in module-scope factory functions above; the
 * hook body is only responsible for managing state and wiring the pieces together.
 */
export default function useNearbyVessels(): NearbyVesselsHookResult {
  const { canAccessFeatureAisApiNearbyVessels } = useAccessControl();
  const useInternalApi = canAccessFeatureAisApiNearbyVessels.enabled;

  const dispatch = useAppDispatch();
  const { loading, error, vessels, formValues } = useAppSelector(
    (state) => state.nearbyVessels
  );
  // Ref so websocket callbacks always see the latest dispatch (avoids stale closures).
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  // V1-specific state
  const [websocketId, setWebsocketId] = useState<string | undefined>(undefined);

  // V2-specific state
  const [aisBaseUrl, setAisBaseUrl] = useState<string | undefined>(undefined);
  const [wsUrl, setWsUrl] = useState<string | undefined>(undefined);
  const [mountWebsocket, setMountWebsocket] = useState(false);
  const dispatchQueryRef = useRef<(() => Promise<boolean>) | null>(null);

  const connectionActive = useInternalApi ? mountWebsocket : !!websocketId;
  const longWaitMessage = useLongWaitTimer(connectionActive);

  useMapLayerCircle(
    MapLayer.NEARBY_VESSELS_AREA,
    formValues.centrePoint,
    formValues.radius
  );

  useEffect(() => {
    if (!loading && !error) renderNearbyVessels(vessels);
  }, [vessels, loading, error]);

  const onSubmit = useInternalApi
    ? createV2SubmitHandler(
        dispatch,
        aisBaseUrl,
        setAisBaseUrl,
        setWsUrl,
        (fn) => {
          dispatchQueryRef.current = fn;
        },
        setMountWebsocket
      )
    : createV1SubmitHandler(dispatch, setWebsocketId);

  const messageHandler = useInternalApi
    ? createV2MessageHandler(dispatchRef, setMountWebsocket)
    : createV1MessageHandler(dispatchRef, setWebsocketId);

  const onReset = () => {
    dispatch(setVessels(null));
    clearNearbyVessels();
    if (useInternalApi) setMountWebsocket(false);
    else setWebsocketId(undefined);
  };

  const onCancel = useInternalApi
    ? () => {
        setMountWebsocket(false);
        dispatch(setLoading(false));
        dispatch(setError(false));
      }
    : undefined;

  const websocketProps = useInternalApi
    ? buildV2WebsocketProps(
        mountWebsocket,
        wsUrl,
        dispatchRef,
        dispatchQueryRef,
        setMountWebsocket,
        messageHandler
      )
    : buildV1WebsocketProps(websocketId, messageHandler);

  return {
    vessels: toVesselItems(vessels),
    loading,
    error,
    formValues,
    onFormValuesChange: (values) => dispatch(setFormValues(values)),
    onSubmit,
    onReset,
    onCancel,
    longWaitMessage,
    pendingCount: loading ? vessels?.length ?? null : null,
    websocketProps,
  };
}
